import React, { useState, useEffect } from 'react';
import {
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
  Send as SendIcon,
  Cancel as CancelIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as DeclineIcon,
  Schedule as PendingIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Project, User, ProjectInvitation } from '../../types';
import { apiService } from '../../services/api';

interface ProjectInvitationsProps {
  project: Project;
  currentUser: User;
  onClose?: () => void;
}

export const ProjectInvitations: React.FC<ProjectInvitationsProps> = ({
  project,
  currentUser,
  onClose
}) => {
  const [invitations, setInvitations] = useState<ProjectInvitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmails, setInviteEmails] = useState('');
  const [inviteMessage, setInviteMessage] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  useEffect(() => {
    loadInvitations();
  }, [project.id]);

  const loadInvitations = async () => {
    try {
      setIsLoading(true);
      const data = await apiService.getProjectInvitations(
        project.id,
        selectedStatus === 'all' ? undefined : selectedStatus
      );
      setInvitations(data);
    } catch (error) {
      console.error('Failed to load invitations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteUsers = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inviteEmails.trim()) return;
    
    try {
      setIsInviting(true);
      
      // Parse emails (comma or newline separated)
      const emails = inviteEmails
        .split(/[,\n]/)
        .map(email => email.trim())
        .filter(email => email && /\S+@\S+\.\S+/.test(email));
      
      if (emails.length === 0) {
        alert('Please enter valid email addresses');
        return;
      }
      
      await apiService.inviteUsersToProject(project.id, emails, inviteMessage);
      
      // Reset form
      setInviteEmails('');
      setInviteMessage('');
      setShowInviteForm(false);
      
      // Reload invitations
      await loadInvitations();
      
      alert(`Successfully sent ${emails.length} invitation${emails.length !== 1 ? 's' : ''}!`);
      
    } catch (error) {
      console.error('Failed to send invitations:', error);
      alert('Failed to send invitations. Please try again.');
    } finally {
      setIsInviting(false);
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!confirm('Are you sure you want to cancel this invitation?')) return;
    
    try {
      await apiService.cancelInvitation(invitationId);
      await loadInvitations();
    } catch (error) {
      console.error('Failed to cancel invitation:', error);
      alert('Failed to cancel invitation. Please try again.');
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await apiService.resendInvitation(invitationId);
      await loadInvitations();
      alert('Invitation resent successfully!');
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      alert('Failed to resend invitation. Please try again.');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return <PendingIcon className="text-yellow-600" style={{ fontSize: 16 }} />;
      case 'accepted':
        return <CheckCircleIcon className="text-green-600" style={{ fontSize: 16 }} />;
      case 'declined':
        return <DeclineIcon className="text-red-600" style={{ fontSize: 16 }} />;
      case 'cancelled':
        return <CancelIcon className="text-gray-600" style={{ fontSize: 16 }} />;
      default:
        return <PendingIcon className="text-gray-600" style={{ fontSize: 16 }} />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'accepted':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'declined':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expiresAt: string) => {
    return new Date(expiresAt) < new Date();
  };

  const filteredInvitations = invitations.filter(inv => {
    if (selectedStatus === 'all') return true;
    return inv.status === selectedStatus;
  });

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <EmailIcon className="text-blue-600" style={{ fontSize: 24 }} />
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Project Invitations</h3>
            <p className="text-sm text-gray-600">Manage user invitations for {project.name}</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowInviteForm(!showInviteForm)}
            className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <PersonAddIcon style={{ fontSize: 16 }} />
            <span>Invite Users</span>
          </button>
          
          {onClose && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <CloseIcon style={{ fontSize: 20 }} />
            </button>
          )}
        </div>
      </div>

      {/* Invite Form */}
      {showInviteForm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
          <h4 className="text-lg font-medium text-blue-900 mb-4">Invite Users by Email</h4>
          
          <form onSubmit={handleInviteUsers} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Addresses *
              </label>
              <textarea
                value={inviteEmails}
                onChange={(e) => setInviteEmails(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Enter email addresses separated by commas or new lines&#10;example@company.com, user@domain.com"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Separate multiple emails with commas or new lines
              </p>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Personal Message (Optional)
              </label>
              <textarea
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Add a personal message to the invitation..."
              />
            </div>
            
            <div className="flex items-center justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowInviteForm(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isInviting}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {isInviting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <SendIcon style={{ fontSize: 16 }} />
                    <span>Send Invitations</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filter and Stats */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-4">
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="all">All Invitations</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="declined">Declined</option>
            <option value="cancelled">Cancelled</option>
          </select>
          
          <button
            onClick={loadInvitations}
            className="flex items-center space-x-2 px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors text-sm"
          >
            <RefreshIcon style={{ fontSize: 16 }} />
            <span>Refresh</span>
          </button>
        </div>
        
        <div className="text-sm text-gray-600">
          {filteredInvitations.length} invitation{filteredInvitations.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Invitations List */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
          <p className="text-gray-600">Loading invitations...</p>
        </div>
      ) : filteredInvitations.length === 0 ? (
        <div className="text-center py-12">
          <EmailIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h4 className="text-lg font-medium text-gray-900 mb-2">No invitations found</h4>
          <p className="text-gray-600 mb-4">
            {selectedStatus === 'all' 
              ? 'No invitations have been sent for this project yet.'
              : `No ${selectedStatus} invitations found.`
            }
          </p>
          <button
            onClick={() => setShowInviteForm(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Send First Invitation
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredInvitations.map((invitation) => (
            <div key={invitation.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <EmailIcon className="text-gray-400" style={{ fontSize: 18 }} />
                    <span className="font-medium text-gray-900">{invitation.email}</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(invitation.status)}`}>
                      {getStatusIcon(invitation.status)}
                      <span className="ml-1 capitalize">{invitation.status}</span>
                    </span>
                    {isExpired(invitation.expires_at) && invitation.status === 'pending' && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                        Expired
                      </span>
                    )}
                  </div>
                  
                  <div className="text-sm text-gray-600 space-y-1">
                    <p>Invited on {formatDate(invitation.created_at)}</p>
                    <p>Expires on {formatDate(invitation.expires_at)}</p>
                    {invitation.message && (
                      <p className="italic">"{invitation.message}"</p>
                    )}
                    {invitation.accepted_at && (
                      <p className="text-green-600">Accepted on {formatDate(invitation.accepted_at)}</p>
                    )}
                    {invitation.declined_at && (
                      <p className="text-red-600">Declined on {formatDate(invitation.declined_at)}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center space-x-2">
                  {invitation.status === 'pending' && !isExpired(invitation.expires_at) && (
                    <>
                      <button
                        onClick={() => handleResendInvitation(invitation.id)}
                        className="flex items-center space-x-1 px-3 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors text-sm"
                        title="Resend invitation"
                      >
                        <RefreshIcon style={{ fontSize: 14 }} />
                        <span>Resend</span>
                      </button>
                      
                      <button
                        onClick={() => handleCancelInvitation(invitation.id)}
                        className="flex items-center space-x-1 px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors text-sm"
                        title="Cancel invitation"
                      >
                        <DeleteIcon style={{ fontSize: 14 }} />
                        <span>Cancel</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Stats */}
      {invitations.length > 0 && (
        <div className="mt-6 pt-6 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Total', count: invitations.length, color: 'text-gray-600' },
              { label: 'Pending', count: invitations.filter(i => i.status === 'pending').length, color: 'text-yellow-600' },
              { label: 'Accepted', count: invitations.filter(i => i.status === 'accepted').length, color: 'text-green-600' },
              { label: 'Declined', count: invitations.filter(i => i.status === 'declined').length, color: 'text-red-600' }
            ].map((stat, index) => (
              <div key={index} className="text-center">
                <div className={`text-2xl font-bold ${stat.color}`}>{stat.count}</div>
                <div className="text-sm text-gray-500">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};